import React, { useContext, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { ContextMenuState, OverlayMenuEnum, OverlayMenuState, TankPlan } from 'src/types/ViewModel/Vessel/TankPlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { MenuPosition } from 'src/types/ViewModel/Stowage/MenuPosition';
import { OperationListColumns } from 'src/types/ViewModel/Stowage/OperationListColumns';
import { SortStowageColumnEnum } from 'src/enums/stowage/sortStowageColumnEnum';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { TankPlanFilter } from 'src/enums/stowage/tankPlanFilter';
import { Manifest } from 'src/types/ViewModel/Stowage/Manifest/Manifest';
import { RotationCallStatus } from 'src/types/ViewModel/Stowage/Rotation/RotationCallStatus';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { AppModalData } from 'src/types/ModalData';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { ChangeLogProps } from 'src/components/stowage/common/changeLog/ChangeLog';
import { MasterPlanSyncData } from 'src/types/ViewModel/Stowage/MasterPlanSyncData';
import { OperationPanelContext } from 'src/types/ViewModel/Stowage/OperationPanelContext';
import { Position } from 'src/types/ViewModel/Stowage/Position';
import { ColumnWidths } from 'src/types/ViewModel/Stowage/ColumnWidths';
import { getSavedColumnWidths } from 'src/components/stowage/operationListView/utils/columnWidthUtils';
import { ColumnConfig } from 'src/components/stowage/operationListView/utils/columnConfig';

type StowageContextType = {
    tankPlan: TankPlan;
    setTankPlan: (tankPlan: TankPlan | null) => void;
    selectedTankPlanCondition: TankPlanCondition;
    setSelectedTankPlanCondition: (condition: TankPlanCondition) => void;
    reportTankPlanCondition: TankPlanCondition;
    setReportTankPlanCondition: (condition: TankPlanCondition) => void;
    selectedTankPlanFilters: TankPlanFilter[];
    setSelectedTankPlanFilters: (filters: TankPlanFilter[]) => void;
    tankContextMenuState: ContextMenuState;
    setTankContextMenuState: (contextMenuState: ContextMenuState) => void;
    operationListContextMenuState: ContextMenuState;
    setOperationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    berthOperationListContextMenuState: ContextMenuState;
    setBerthOperationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    violationListContextMenuState: ContextMenuState;
    setViolationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    tankPlanBottomContextMenuState: ContextMenuState;
    setTankPlanBottomContextMenuState: (contextMenuState: ContextMenuState) => void;
    pageHeaderContextMenuState: ContextMenuState;
    setPageHeaderContextMenuState: (contextMenuState: ContextMenuState) => void;
    overlayMenuState: OverlayMenuState;
    setOverlayMenuState: (overlayMenuState: OverlayMenuState) => void;
    isTradeNameSelectionListOpen: boolean | null;
    setIsTradeNameSelectionListOpen: (isVisible: boolean | null) => void;
    isStowagePlanListMenuOpen: boolean | null;
    setIsStowagePlanListMenuOpen: (isVisible: boolean | null) => void;
    isCopyStowagePlanMenuOpen: boolean | null;
    setIsCopyStowagePlanMenuOpen: (isVisible: boolean | null) => void;
    isRenameStowagePlanMenuOpen: boolean | null;
    setIsRenameStowagePlanMenuOpen: (isVisible: boolean | null) => void;
    isPublishPromoteOpen: boolean | null;
    setIsPublishPromoteOpen: (isVisible: boolean | null) => void;
    isCargoDetailsPanelOpen: boolean | null;
    setIsCargoDetailsPanelOpen: (showCargo: boolean | null) => void;
    rotationMenuOpen: MenuPosition | null;
    setRotationMenuOpen: (menu: MenuPosition | null) => void;
    triggerChangeLogProps: ChangeLogProps | null;
    setTriggerChangeLogProps: (props: ChangeLogProps | null) => void;
    isUtilizationOpen: boolean | null;
    setIsUtilizationOpen: (isVisible: boolean | null) => void;
    viewCargoOperation: RotationCargoOperation | null;
    setViewCargoOperation: (cargoOperation: RotationCargoOperation | null) => void;
    activeStowagePlan: StowagePlan | null;
    setActiveStowagePlan: (plan: StowagePlan | null) => void;
    manifest: Manifest | null;
    setManifest: (manifest: Manifest | null) => void;
    stowagePlans: StowagePlan[] | null;
    setStowagePlans: (plan: StowagePlan[] | null) => void;
    activeOperationsFilters: string[] | null;
    setActiveOperationsFilters: (filter: string[] | null) => void;
    rotationCallStatuses: RotationCallStatus[] | null;
    setRotationCallStatuses: (rotationCallStatuses: RotationCallStatus[] | null) => void;
    isShareMenuOpen: boolean | null;
    setIsShareMenuOpen: (isVisible: boolean | null) => void;
    isFillPanelOpen: boolean | null;
    setIsFillPanelOpen: (isVisible: boolean | null) => void;
    isColumnsPanelOpen: boolean | null;
    setIsColumnsPanelOpen: (isVisible: boolean | null) => void;
    isShipFigurePanelOpen: boolean | null;
    setIsShipFigurePanelOpen: (isVisible: boolean | null) => void;
    isBlockTankPanelOpen: boolean | null;
    setIsAdjustCargoTemperaturePanelOpen: (isVisible: boolean | null) => void;
    isAdjustCargoTemperaturePanelOpen: boolean | null;
    setIsShowOperationForCargoPanelOpen: (isVisible: boolean | null) => void;
    isShowOperationForCargoPanelOpen: boolean | null;
    isCargoColorPanelOpen: boolean | null;
    setIsCargoColorPanelOpen: (isVisible: boolean | null) => void;
    setIsBlockTankPanelOpen: (isVisible: boolean | null) => void;
    operationListColumns: OperationListColumns | null;
    setOperationListColumns: (op: OperationListColumns | null) => void;
    tankPlanWindowResized: string | null;
    setTankPlanWindowResized: (nr: string | null) => void;
    operationListSortColumn: SortStowageColumnEnum;
    setOperationListSortColumn: (nr: SortStowageColumnEnum) => void;
    isOperationListSortDesc: boolean | null;
    setIsOperationListSortDesc: (isVisible: boolean | null) => void;
    rightViewWidth: number | null;
    setRightViewWidth: (width: number | null) => void;
    rightViewHeight: number | null;
    setRightViewHeight: (width: number | null) => void;
    tankplanViewWidth: number | null;
    setTankplanViewWidth: (width: number | null) => void;
    tankplanViewHeight: number | null;
    setTankplanViewHeight: (width: number | null) => void;
    isStowagePlanUpdated: { value: boolean } | null;
    setIsStowagePlanUpdated: (value: { value: boolean | null }) => void;
    tankPlanZoomInScale: number | null;
    setTankPlanZoomInScale: (value: number | null) => void;
    tankPlanZoomOutScale: number | null;
    setTankPlanZoomOutScale: (value: number | null) => void;
    tankPlanReset: boolean | null;
    setTankPlanReset: (value: boolean | null) => void;
    tankPlanMove: boolean | null;
    setTankPlanMove: (value: boolean | null) => void;
    isLeftPanelHidden: boolean | null;
    setIsLeftPanelHidden: (isVisible: boolean | null) => void;
    isFilterPanelOpen: boolean | null;
    setIsFilterPanelOpen: (isVisible: boolean | null) => void;
    isTankPlanFilterPanelOpen: boolean | null;
    setIsTankPlanFilterPanelOpen: (isVisible: boolean | null) => void;
    stowagePlanChanges: StowagePlanChange[];
    setStowagePlanChanges: (stowagePlanChanges: StowagePlanChange[]) => void;
    isCargoOperationPanelOpen: OperationPanelContext | null;
    setIsCargoOperationPanelOpen: (panelContext: OperationPanelContext) => void;
    isSplitCargoOperationPanelOpen: boolean | null;
    setIsSplitCargoOperationPanelOpen: (isVisible: boolean | null) => void;
    stowageModalData: AppModalData | null;
    setStowageModalData: (stowageModalData: AppModalData | null) => void;
    imosSyncChanges: StowagePlanChange[];
    setImosSyncChanges: (stowagePlanChanges: StowagePlanChange[]) => void;
    masterPlanSyncData: MasterPlanSyncData | null;
    setMasterPlanSyncData: (masterPlanSyncData: MasterPlanSyncData | null) => void;
    unavailableTanks: Tank[] | null;
    setUnavailableTanks: (availableTanks: Tank[] | null) => void;
    unavailableTankViolations: Map<number, Violation[]> | null;
    setUnavailableTankViolations: (violations: Map<number, Violation[]> | null) => void;
    isPrintPanelOpen: boolean;
    setIsPrintPanelOpen: (isOpen: boolean) => void;
    useDynamicTankScaling: boolean;
    setUseDynamicTankScaling: (value: boolean) => void;
    zoomedTank: Tank | null;
    setZoomedTank: (tank: Tank | null) => void;
    mouseClickPosition: Position | null;
    setMouseClickPosition: (pos: Position | null) => void;
    operationListColumnWidths: ColumnWidths;
    setOperationListColumnWidths: (widths: ColumnWidths) => void;
    orderedColumns: ColumnConfig[];
    setOrderedColumns: (columns: ColumnConfig[]) => void;
    isGeneratingTankPlanReport: boolean;
    setIsGeneratingTankPlanReport: (isGenerating: boolean) => void;
    popOutOperationList: boolean;
    setPopOutOperationList: (isOpen: boolean) => void;
    portalRootHeight: number;
    setPortalRootHeight: (height: number) => void;
    hasClosedOldStowagePlanModal: boolean;
    setHasClosedOldStowagePlanModal: (hasClosed: boolean) => void;
    loadedMasterStowagePlan: StowagePlan;
    setLoadedMasterStowagePlan: (stowagePlan: StowagePlan) => void;
};

export const StowageContext = React.createContext<StowageContextType>(undefined!);

export const StowageProvider = ({ children }: ContextProps) => {
    const [tankPlan, setTankPlan] = useState<TankPlan>(null);
    const [selectedTankPlanCondition, setSelectedTankPlanCondition] = useState<TankPlanCondition>(TankPlanCondition.Departure);
    const [reportTankPlanCondition, setReportTankPlanCondition] = useState<TankPlanCondition | null>(null);
    const [selectedTankPlanFilters, setSelectedTankPlanFilters] = useState<TankPlanFilter[]>([]);
    const [operationListSortColumn, setOperationListSortColumn] = useState<SortStowageColumnEnum>(SortStowageColumnEnum.TradeName);
    const [tankContextMenuState, setTankContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [operationListContextMenuState, setOperationListContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [berthOperationListContextMenuState, setBerthOperationListContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [violationListContextMenuState, setViolationListContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [tankPlanBottomContextMenuState, setTankPlanBottomContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [pageHeaderContextMenuState, setPageHeaderContextMenuState] = useState<ContextMenuState>({
        visible: false,
        tank: null,
        cargoContents: [],
        cargoOperationGuid: crypto.randomUUID(),
        berthCall: null,
        x: 0,
        y: 0,
        violations: null,
    });
    const [overlayMenuState, setOverlayMenuState] = useState<OverlayMenuState>({
        visible: false,
        type: OverlayMenuEnum.TankDetails,
        tank: null,
        cargoContents: null,
    });
    const [isTradeNameSelectionListOpen, setIsTradeNameSelectionListOpen] = useState<boolean | null>(null);
    const [isStowagePlanListMenuOpen, setIsStowagePlanListMenuOpen] = useState<boolean | null>(null);
    const [isCopyStowagePlanMenuOpen, setIsCopyStowagePlanMenuOpen] = useState<boolean | null>(null);
    const [isRenameStowagePlanMenuOpen, setIsRenameStowagePlanMenuOpen] = useState<boolean | null>(null);
    const [isPublishPromoteOpen, setIsPublishPromoteOpen] = useState<boolean | null>(null);
    const [isCargoDetailsPanelOpen, setIsCargoDetailsPanelOpen] = useState<boolean | null>(null);
    const [rotationMenuOpen, setRotationMenuOpen] = useState<MenuPosition | null>(null);
    const [triggerChangeLogProps, setTriggerChangeLogProps] = useState<ChangeLogProps | null>(null);
    const [isUtilizationOpen, setIsUtilizationOpen] = useState<boolean | null>(null);
    const [viewCargoOperation, setViewCargoOperation] = useState<RotationCargoOperation | null>(null);
    const [activeStowagePlan, setActiveStowagePlan] = useState<StowagePlan | null>(null);
    const [manifest, setManifest] = useState<Manifest | null>(null);
    const [stowagePlans, setStowagePlans] = useState<StowagePlan[] | null>(null);
    const [activeOperationsFilters, setActiveOperationsFilters] = useState<string[] | null>(['loading', 'unloading', 'transit', 'transfer']);
    const [rotationCallStatuses, setRotationCallStatuses] = useState<RotationCallStatus[] | null>(null);
    const [isShareMenuOpen, setIsShareMenuOpen] = useState<boolean | null>(null);
    const [isFillPanelOpen, setIsFillPanelOpen] = useState<boolean | null>(null);
    const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState<boolean | null>(null);
    const [isShipFigurePanelOpen, setIsShipFigurePanelOpen] = useState<boolean | null>(null);
    const [isBlockTankPanelOpen, setIsBlockTankPanelOpen] = useState<boolean | null>(null);
    const [isAdjustCargoTemperaturePanelOpen, setIsAdjustCargoTemperaturePanelOpen] = useState<boolean | null>(null);
    const [isCargoColorPanelOpen, setIsCargoColorPanelOpen] = useState<boolean | null>(null);
    const [operationListColumns, setOperationListColumns] = useState<OperationListColumns | null>(null);
    const [tankPlanWindowResized, setTankPlanWindowResized] = useState<string | null>(null);
    const [isOperationListSortDesc, setIsOperationListSortDesc] = useState<boolean | null>(null);
    const [rightViewWidth, setRightViewWidth] = useState<number | null>(null);
    const [rightViewHeight, setRightViewHeight] = useState<number | null>(null);
    const [tankplanViewWidth, setTankplanViewWidth] = useState<number | null>(null);
    const [tankplanViewHeight, setTankplanViewHeight] = useState<number | null>(null);
    const [isStowagePlanUpdated, setIsStowagePlanUpdated] = useState<{ value: boolean } | null>(null);
    const [tankPlanZoomInScale, setTankPlanZoomInScale] = useState<number | null>(1);
    const [tankPlanZoomOutScale, setTankPlanZoomOutScale] = useState<number | null>(1);
    const [isLeftPanelHidden, setIsLeftPanelHidden] = useState<boolean | null>(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState<boolean | null>(false);
    const [isTankPlanFilterPanelOpen, setIsTankPlanFilterPanelOpen] = useState<boolean | null>(false);
    const [stowagePlanChanges, setStowagePlanChanges] = useState<StowagePlanChange[]>([]);
    const [tankPlanReset, setTankPlanReset] = useState<boolean | null>(null);
    const [tankPlanMove, setTankPlanMove] = useState<boolean | null>(false);
    const [isCargoOperationPanelOpen, setIsCargoOperationPanelOpen] = useState<OperationPanelContext | null>(null);
    const [isSplitCargoOperationPanelOpen, setIsSplitCargoOperationPanelOpen] = useState<boolean | null>(null);
    const [stowageModalData, setStowageModalData] = useState<AppModalData | null>(null);
    const [imosSyncChanges, setImosSyncChanges] = useState<StowagePlanChange[]>([]);
    const [masterPlanSyncData, setMasterPlanSyncData] = useState<MasterPlanSyncData | null>(null);
    const [unavailableTanks, setUnavailableTanks] = useState<Tank[] | null>(null);
    const [unavailableTankViolations, setUnavailableTankViolations] = useState<Map<number, Violation[]> | null>(null);
    const [isPrintPanelOpen, setIsPrintPanelOpen] = useState<boolean>(false);
    const [useDynamicTankScaling, setUseDynamicTankScaling] = useState<boolean>(false);
    const [zoomedTank, setZoomedTank] = useState<Tank | null>(null);
    const [mouseClickPosition, setMouseClickPosition] = useState<Position | null>(null);
    const [operationListColumnWidths, setOperationListColumnWidths] = useState<ColumnWidths>(getSavedColumnWidths());
    const [orderedColumns, setOrderedColumns] = useState<ColumnConfig[]>([]);
    const [isGeneratingTankPlanReport, setIsGeneratingTankPlanReport] = useState<boolean>(false);
    const [popOutOperationList, setPopOutOperationList] = useState<boolean>(false);
    const [portalRootHeight, setPortalRootHeight] = useState<number | null>(null);
    const [hasClosedOldStowagePlanModal, setHasClosedOldStowagePlanModal] = useState<boolean>(false);
    const [loadedMasterStowagePlan, setLoadedMasterStowagePlan] = useState<StowagePlan | null>(null);
    const [isShowOperationForCargoPanelOpen, setIsShowOperationForCargoPanelOpen] = useState<boolean | null>(null);

    return (
        <StowageContext.Provider
            value={{
                tankPlan,
                setTankPlan,
                selectedTankPlanCondition,
                setSelectedTankPlanCondition,
                reportTankPlanCondition,
                setReportTankPlanCondition,
                selectedTankPlanFilters,
                setSelectedTankPlanFilters,
                tankContextMenuState,
                setTankContextMenuState,
                operationListContextMenuState,
                setOperationListContextMenuState,
                berthOperationListContextMenuState,
                setBerthOperationListContextMenuState,
                violationListContextMenuState,
                setViolationListContextMenuState,
                overlayMenuState,
                setOverlayMenuState,
                isTradeNameSelectionListOpen,
                setIsTradeNameSelectionListOpen,
                isStowagePlanListMenuOpen,
                setIsStowagePlanListMenuOpen,
                isCopyStowagePlanMenuOpen,
                setIsCopyStowagePlanMenuOpen,
                isRenameStowagePlanMenuOpen,
                setIsRenameStowagePlanMenuOpen,
                isPublishPromoteOpen,
                setIsPublishPromoteOpen,
                isCargoDetailsPanelOpen,
                setIsCargoDetailsPanelOpen,
                rotationMenuOpen,
                setRotationMenuOpen,
                triggerChangeLogProps,
                setTriggerChangeLogProps,
                isUtilizationOpen,
                setIsUtilizationOpen,
                viewCargoOperation,
                setViewCargoOperation,
                activeStowagePlan,
                setActiveStowagePlan,
                manifest,
                setManifest,
                stowagePlans,
                setStowagePlans,
                activeOperationsFilters,
                setActiveOperationsFilters,
                rotationCallStatuses,
                setRotationCallStatuses,
                isShareMenuOpen,
                setIsShareMenuOpen,
                isFillPanelOpen,
                setIsFillPanelOpen,
                isFilterPanelOpen,
                isTankPlanFilterPanelOpen,
                setIsTankPlanFilterPanelOpen,
                setIsFilterPanelOpen,
                isShipFigurePanelOpen,
                setIsShipFigurePanelOpen,
                isColumnsPanelOpen,
                setIsColumnsPanelOpen,
                operationListColumns,
                setOperationListColumns,
                tankPlanWindowResized,
                setTankPlanWindowResized,
                operationListSortColumn,
                setOperationListSortColumn,
                isOperationListSortDesc,
                setIsOperationListSortDesc,
                isBlockTankPanelOpen,
                setIsBlockTankPanelOpen,
                isAdjustCargoTemperaturePanelOpen,
                setIsAdjustCargoTemperaturePanelOpen,
                isCargoOperationPanelOpen,
                setIsCargoOperationPanelOpen,
                isCargoColorPanelOpen,
                setIsCargoColorPanelOpen,
                tankPlanBottomContextMenuState,
                setTankPlanBottomContextMenuState,
                pageHeaderContextMenuState,
                setPageHeaderContextMenuState,
                rightViewWidth,
                setRightViewWidth,
                rightViewHeight,
                setRightViewHeight,
                tankplanViewWidth,
                setTankplanViewWidth,
                tankplanViewHeight,
                setTankplanViewHeight,
                isStowagePlanUpdated,
                setIsStowagePlanUpdated,
                tankPlanZoomInScale,
                setTankPlanZoomInScale,
                tankPlanZoomOutScale,
                setTankPlanZoomOutScale,
                tankPlanReset,
                setTankPlanReset,
                tankPlanMove,
                setTankPlanMove,
                isLeftPanelHidden,
                setIsLeftPanelHidden,
                stowagePlanChanges,
                setStowagePlanChanges,
                isSplitCargoOperationPanelOpen,
                setIsSplitCargoOperationPanelOpen,
                isShowOperationForCargoPanelOpen,
                setIsShowOperationForCargoPanelOpen,
                stowageModalData,
                setStowageModalData,
                imosSyncChanges,
                setImosSyncChanges,
                masterPlanSyncData,
                setMasterPlanSyncData,
                unavailableTanks,
                setUnavailableTanks,
                unavailableTankViolations,
                setUnavailableTankViolations,
                isPrintPanelOpen,
                setIsPrintPanelOpen,
                useDynamicTankScaling,
                setUseDynamicTankScaling,
                zoomedTank,
                setZoomedTank,
                mouseClickPosition,
                setMouseClickPosition,
                operationListColumnWidths,
                setOperationListColumnWidths,
                orderedColumns,
                setOrderedColumns,
                isGeneratingTankPlanReport,
                setIsGeneratingTankPlanReport,
                popOutOperationList,
                setPopOutOperationList,
                portalRootHeight,
                setPortalRootHeight,
                hasClosedOldStowagePlanModal,
                setHasClosedOldStowagePlanModal,
                loadedMasterStowagePlan,
                setLoadedMasterStowagePlan,
            }}
        >
            {children}
        </StowageContext.Provider>
    );
};

export const useStowageStore = () => useContext(StowageContext);
